<script setup lang="ts">
import { computed, withDefaults } from 'vue';
import PrevIcon from './svg/AngleLeft.vue';
import NextIcon from './svg/AngleRight.vue';
import Ellipsis from './svg/Ellipsis.vue';

type Props = {
  modelValue: number,
  isDisabled?: boolean,
  maxVisibleButtons?: number,
  pageSize: number,
  totalItems: number,
}

const props = withDefaults(defineProps<Props>(), {
  maxVisibleButtons: 3
});


// ++++ Computed
const isAtFirstPage = computed(() => {
  return props.modelValue === 1;
});

const isAtLastPage = computed(() => {
  return props.modelValue === totalPages.value || totalPages.value === 0;
});

const isTooFarFromStart = computed(() => {
  return props.modelValue > props.maxVisibleButtons + 1;
});

const isTooFarFromEnd = computed(() => {
  return (totalPages.value - endPage.value) >= 2 && props.modelValue !== totalPages.value;
});

const startPage = computed(() => {
  if (props.modelValue <= Math.ceil(props.maxVisibleButtons / 2) || props.maxVisibleButtons >= totalPages.value) {
    return 2;
  }

  if (isAtLastPage.value) {
    return totalPages.value - props.maxVisibleButtons + 1;
  }

  return props.modelValue - 1;
});

const endPage = computed(() => {
  if (props.modelValue <= 12) {
    return Math.min(props.maxVisibleButtons, totalPages.value - 1);
  }

  return Math.min(startPage.value + props.maxVisibleButtons - 1, totalPages.value - 1);
});

type PageLink = {
  name: number,
  isActive: boolean
}

const pageLinks = computed<PageLink[]>((): PageLink[] => {
  const range: PageLink[] = [];

  for (
    let i = startPage.value;
    i <= endPage.value;
    i++
  ) {
    range.push({
      name: i,
      isActive: i === props.modelValue
    });
  }

  return range;
});

const showLastPageLink = computed(() => {
  return totalPages.value > 1;
});

const totalPages = computed(() => {
  return Math.ceil(props.totalItems / props.pageSize);
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>();


// ++++ Méthodes
const moveToSelectedPage = (page: number): void => {
  if (props.isDisabled) {
    return;
  }

  emit('update:modelValue', page);
  scrollToTop();
};

const moveToPreviousPage = (step: number = 1): void => {
  if (props.isDisabled) {
    return;
  }

  emit('update:modelValue', props.modelValue - step);
  scrollToTop();
};

const moveToNextPage = (step: number = 1): void => {
  if (props.isDisabled) {
    return;
  }

  emit('update:modelValue', props.modelValue + step);
  scrollToTop();
};

const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};
</script>

<template>
  <nav class="pager"
       :aria-label="$t('pagination.pagination')">
    <a :class="{
         'disabled-arrow': isAtFirstPage || isDisabled,
       }"
       :aria-disabled="isDisabled"
       :aria-label="$t('pagination.previous')"
       href="#"
       @click.prevent="moveToPreviousPage()">
      <PrevIcon></PrevIcon>
    </a>

    <a :class="{
         'disabled-link': isAtFirstPage || isDisabled,
         'faded': isDisabled
       }"
       :aria-current="isAtFirstPage ? 'page' : undefined"
       :aria-disabled="isDisabled"
       href="#"
       @click.prevent="moveToSelectedPage(1)">
      1
    </a>

    <Ellipsis v-if="isTooFarFromStart"
              class="navigationEllipsis"></Ellipsis>
    
    <!-- Liens dynamiques -->
    <template v-if="pageLinks.length > 0">
      <a v-for="(pageLink, index) in pageLinks"
         :key="index"
         :class="{
           'disabled-link': pageLink.isActive,
           'faded': isDisabled
         }"
         :aria-disabled="isDisabled"
         :aria-current="pageLink.isActive ? 'page' : undefined"
         href="#"
         @click.prevent="moveToSelectedPage(pageLink.name)">
        {{ pageLink.name }}
      </a>
    </template>
    <!-- Fin liens dynamiques-->

    <Ellipsis v-if="isTooFarFromEnd"
              class="navigationEllipsis"></Ellipsis>

    <a v-if="showLastPageLink"
       :class="{
         'disabled-link': isAtLastPage,
         'faded': isDisabled
       }"
       :aria-current="isAtLastPage ? 'page' : undefined"
       href="#"
       @click.prevent="moveToSelectedPage(totalPages)">
      {{ totalPages }}
    </a>

    <a :class="{
         'disabled-arrow': isAtLastPage || isDisabled
       }"
       :aria-label="$t('pagination.next')"
       @click.prevent="moveToNextPage()">
      <NextIcon></NextIcon>
    </a>
  </nav>
</template>

<style lang="scss">

</style>